import { Chip, CircularProgress, Stack } from '@mui/material';
import { DataGrid, GridRowSelectionModel } from '@mui/x-data-grid';
import React, { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { REPORTING_HUB_EDIT_URL } from '../../constants/routeUrls';
import { useReportingHubPageContext } from '../../pages/ReportingHubPageContext';
import { ReportTemplate } from '../../validationSchemas/ReportTemplateSchema';

interface User {
  id: number;
  name: string;
}
interface ReportTemplateTableByUserProps {
  userList: User[];
  selectedReportTemplateIds: GridRowSelectionModel;
  setSelectedReportTemplateIds: (ids: GridRowSelectionModel) => void;
}

function ReportTemplateTableByUser({
  userList,
  selectedReportTemplateIds,
  setSelectedReportTemplateIds,
}: ReportTemplateTableByUserProps) {
  const navigate = useNavigate();
  const { reportTemplates: reportTemplatesRaw, isLoading } = useReportingHubPageContext();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  const reportTemplates: ReportTemplate[] = reportTemplatesRaw?.map((item) => item.template?.reportTemplate);

  const rows = reportTemplates?.map((reportTemplate: ReportTemplate) => ({
    ...reportTemplate,
    userName: userList?.find((userItem) => userItem.id === reportTemplate.userId)?.name,
    description: reportTemplate.sheetObjects
      ?.reduce((accumulator, sheetObject) => {
        accumulator.push(sheetObject.sheetName);
        return accumulator;
      }, [])
      .join(', '),
  }));

  const getRowId = (row) => row.templateId;

  const columns = [
    {
      field: 'templateId',
      headerName: 'ID',
      flex: 1,
    },
    {
      field: 'reportName',
      headerName: 'Template Name',
      flex: 3,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 6,
      disableColumnMenu: true,
      hideSortIcons: true,
    },
    {
      field: 'userName',
      headerName: 'Owner',
      renderCell: (params) => (
        <Stack height="100%" justifyContent="center" alignItems="start">
          <Chip
            color="info"
            label={params.value || 'Unknown'}
            size="small"
            sx={{ borderRadius: '4px', fontSize: '10px', fontWeight: '600', cursor: 'pointer' }}
          />
        </Stack>
      ),
      flex: 2,
    },
  ];

  return (
    <>
      {isLoading && <CircularProgress />}
      {!isLoading && (
        <DataGrid
          columns={columns}
          rows={rows}
          getRowId={getRowId}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          checkboxSelection
          rowSelectionModel={selectedReportTemplateIds}
          onRowSelectionModelChange={setSelectedReportTemplateIds}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={(params) =>
            navigate(
              generatePath(REPORTING_HUB_EDIT_URL, {
                reportTemplateId: params.id.toString(),
              })
            )
          }
        />
      )}
    </>
  );
}

export default ReportTemplateTableByUser;
